/* eslint-disable max-lines-per-function */
/* eslint-disable functional/prefer-readonly-type */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useState } from 'react'
import { Grid, Typography, Divider, Box } from '@mui/material'
import dayjs from 'dayjs'
import DetailsData from '~/features/Report/interfaces/DetailsData'
import { useLang } from '~/hooks/useLang'
import { stringToPhoneMask } from '~/utils/strings'

export default function RenderDetaildReport({
  details,
}: {
  readonly details: DetailsData
}) {
  const { translate } = useLang()
  const [valuesDetails, setValuesDetails] = useState<string[]>([])

  useEffect(() => {
    const keyValues = Object.keys(details.value)
    setValuesDetails(keyValues)
  }, [details.value])

  const printValue = (keyDetail, valueDetail) => {
    // Handle date fields
    if (
      keyDetail === 'dispatched_at' ||
      keyDetail === 'closed_at' ||
      keyDetail === 'date_connected' ||
      keyDetail === 'date_disconnected' ||
      keyDetail === 'approach_date'
    ) {
      return (
        <Typography variant="subtitle2">
          <b>{translate(valueName[keyDetail] ?? keyDetail)}: </b>
          {valueDetail
            ? ` ${dayjs(valueDetail).format('DD/MM/YYYY HH:mm:ss')}`
            : ` * ${translate('Uninformed')} *`}
        </Typography>
      )
    }

    // Handle arrays (e.g., qualifier_peoples)
    if (Array.isArray(valueDetail)) {
      return (
        <div>
          <Typography variant="subtitle2">
            <b>{translate(valueName[keyDetail] ?? keyDetail)}:</b>
          </Typography>
          {valueDetail.length === 0 ? (
            <Typography variant="body2" sx={{ ml: 2 }}>
              {`* ${translate('Uninformed')} *`}
            </Typography>
          ) : (
            valueDetail.map((item, idx) => (
              <div key={idx} style={{ marginBottom: 8 }}>
                <Typography variant="body2" sx={{ ml: 2, fontWeight: 'bold' }}>
                  {translate('Person')} {idx + 1}:
                </Typography>
                <Grid container direction="column" sx={{ ml: 4 }}>
                  {Object.entries(item).map(
                    ([propertyKey, propertyValue], entryIndex) => (
                      <Typography key={entryIndex} variant="body2">
                        <b>
                          {translate(
                            valueName[propertyKey] ??
                              (propertyKey === 'cpf'
                                ? 'CPF'
                                : propertyKey === 'cep'
                                ? 'CEP'
                                : propertyKey)
                          )}
                          :
                        </b>
                        {typeof propertyValue === 'boolean' ||
                        propertyValue === 1 ||
                        propertyValue === 0
                          ? propertyValue === true || propertyValue === 1
                            ? translate('Yes')
                            : translate('No')
                          : propertyValue &&
                            typeof propertyValue === 'string' &&
                            /^\d{4}-\d{2}-\d{2}/.test(propertyValue)
                          ? ` ${dayjs(propertyValue).format('DD-MM-YYYY')}`
                          : propertyValue
                          ? ` ${propertyValue}`
                          : ` * ${translate('Uninformed')} *`}
                      </Typography>
                    )
                  )}
                </Grid>
                {valueDetail.length > 1 && idx !== valueDetail.length - 1 && (
                  <Divider sx={{ my: 2, borderBottomWidth: 2 }} />
                )}
              </div>
            ))
          )}
        </div>
      )
    }

    // Handle single objects
    if (typeof valueDetail === 'object' && valueDetail !== null) {
      const objectEntries = Object.entries(valueDetail)
      return (
        <div style={{ marginBottom: 8 }}>
          <Grid container direction="column" sx={{ ml: 2 }}>
            {objectEntries.map(([propertyKey, propertyValue], entryIndex) => (
              <Typography key={entryIndex} variant="body2">
                <b>
                  {translate(
                    valueName[propertyKey] ??
                      (propertyKey === 'cpf'
                        ? 'CPF'
                        : propertyKey === 'cep'
                        ? 'CEP'
                        : propertyKey)
                  )}
                  :
                </b>
                {typeof propertyValue === 'boolean' ||
                propertyValue === 1 ||
                propertyValue === 0
                  ? propertyValue === true || propertyValue === 1
                    ? translate('Yes')
                    : translate('No')
                  : propertyValue &&
                    typeof propertyValue === 'string' &&
                    /^\d{4}-\d{2}-\d{2}/.test(propertyValue)
                  ? ` ${dayjs(propertyValue).format('DD-MM-YYYY')}`
                  : propertyValue &&
                    typeof propertyValue === 'object' &&
                    Object.keys(propertyValue).length > 0
                  ? 'name' in propertyValue &&
                    typeof (propertyValue as { name: string }).name === 'string'
                    ? (propertyValue as { name: string }).name
                    : JSON.stringify(propertyValue)
                  : propertyValue
                  ? ` ${propertyValue}`
                  : ` * ${translate('Uninformed')} *`}
              </Typography>
            ))}
          </Grid>
        </div>
      )
    }

    // Handle phone numbers
    if (keyDetail === 'phone') {
      return (
        <Typography variant="subtitle2">
          <b>{translate(valueName[keyDetail] ?? keyDetail)}:</b>
          {valueDetail
            ? ` ${stringToPhoneMask(valueDetail)}`
            : ` * ${translate('Uninformed')} *`}
        </Typography>
      )
    }

    // Handle total_time and average_time
    if (keyDetail === 'total_time' || keyDetail === 'average_time') {
      return (
        <Typography variant="subtitle2">
          <b>{translate(valueName[keyDetail] ?? keyDetail)}:</b>
          {valueDetail ? ` ${valueDetail}` : ` * ${translate('Uninformed')} *`}
        </Typography>
      )
    }

    // Default case for simple values
    return (
      <Typography variant="subtitle2">
        <b>{translate(valueName[keyDetail] ?? keyDetail)}:</b>
        {valueDetail ? ` ${valueDetail}` : ` * ${translate('Uninformed')} *`}
      </Typography>
    )
  }

  const valueName = {
    entry_origin: 'Entry Origin',
    phone: 'Phone',
    requester: 'Requester',
    agency: 'Agency:name',
    dispatch_group: 'Dispatch Group',
    city: 'City',
    connected_by: 'Connected by',
    address_reference: 'Address Reference',
    approach: 'Approach',
    approach_date: 'Approach Date',
    nickname: 'Nickname',
    qualifier: 'Qualifier',
    total_time: 'Total Time',
    average_time: 'Average Time',
    dispatched_at: 'Dispatched At',
    closed_at: 'Closed At',
    date_connected: 'Date Connected',
    date_disconnected: 'Date Disconnected',
    device_log: 'Device Log',
    device_productivity: 'Device Productivity',
    age: 'Age',
    education: 'Education',
    ethnicity: 'Ethnicity',
    destination: 'Destination',
    occupation: 'Occupation',
    owner: 'Owner',
    circunstance: 'Circunstance',
    quantity: 'Quantity',
    state: 'State',
    has_mental_illness: 'Mental Illness',
    has_physical_illness: 'Physical Illness',
    arrested: 'Arrested',
    is_lgbt: 'LGBTQIAP+',
    is_public_agent: 'Public Agent',
    other_document: 'other_document',
    other_document_type: 'Document Type',
    people_circunstance: 'People Circunstance',
    action_taken: 'Action Taken',
    organization: 'Auxiliary Organ',
    drug_type: 'Drug Type',
    specie: 'Specie',
    breed: 'Breed',
  }

  return (
    <Grid container>
      <Grid container paddingY={1} marginLeft={1.5}>
        <Grid item>
          <>
            {valuesDetails.map((keyDetail, index) => {
              return (
                <Box key={keyDetail}>
                  {printValue(keyDetail, details.value[keyDetail])}
                  {valuesDetails.length - 1 !== index && (
                    <Divider sx={{ my: 2, borderBottomWidth: 2 }} />
                  )}
                </Box>
              )
            })}
          </>
        </Grid>
      </Grid>
    </Grid>
  )
}
